// src/components/ui/Table.tsx
import React from 'react';

// ==================== Table Root ====================
interface TableProps {
  children: React.ReactNode;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
}

const Table: React.FC<TableProps> & {
  Head: typeof TableHead;
  Body: typeof TableBody;
  Row: typeof TableRow;
  Header: typeof TableHeader;
  Cell: typeof TableCell;
  Empty: typeof TableEmpty;
} = ({ children, className = '', striped = false, hoverable = true, compact = false }) => {
  return (
    <div className="overflow-x-auto">
      <table
        className={`
          w-full text-sm text-left
          ${striped ? 'table-striped' : ''}
          ${hoverable ? 'table-hoverable' : ''}
          ${compact ? 'table-compact' : ''}
          ${className}
        `}
      >
        {children}
      </table>
    </div>
  );
};

// ==================== Table Head ====================
interface TableHeadProps {
  children: React.ReactNode;
  className?: string;
}

const TableHead: React.FC<TableHeadProps> = ({ children, className = '' }) => {
  return (
    <thead className={`bg-neutral-bg ${className}`}>
      {children}
    </thead>
  );
};

// ==================== Table Body ====================
interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

const TableBody: React.FC<TableBodyProps> = ({ children, className = '' }) => {
  return <tbody className={className}>{children}</tbody>;
};

// ==================== Table Row ====================
interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
  highlight?: boolean;
}

const TableRow: React.FC<TableRowProps> = ({
  children,
  className = '',
  onClick,
  selected = false,
  highlight = false,
}) => {
  return (
    <tr
      onClick={onClick}
      className={`
        border-b border-neutral-border transition-colors duration-150
        ${onClick ? 'cursor-pointer' : ''}
        ${selected ? 'bg-teal-main/5 border-l-2 border-l-teal-main' : ''}
        ${highlight ? 'bg-warning/5' : ''}
        ${!selected && !highlight ? 'hover:bg-neutral-bg/50' : ''}
        ${className}
      `}
    >
      {children}
    </tr>
  );
};

// ==================== Table Header Cell ====================
interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className = '',
  sortable = false,
  sortDirection = null,
  onSort,
  align = 'left',
  width,
}) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <th
      onClick={sortable ? onSort : undefined}
      style={width ? { width } : undefined}
      className={`
        px-4 py-3 text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary
        ${alignClasses[align]}
        ${sortable ? 'cursor-pointer hover:text-teal-main select-none group' : ''}
        ${className}
      `}
    >
      <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        {children}
        {sortable && (
          <span className="flex flex-col">
            <svg
              className={`w-3 h-3 -mb-1 ${sortDirection === 'asc' ? 'text-teal-main' : 'text-neutral-border group-hover:text-neutral-text-secondary'}`}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M7 14l5-5 5 5z" />
            </svg>
            <svg
              className={`w-3 h-3 ${sortDirection === 'desc' ? 'text-teal-main' : 'text-neutral-border group-hover:text-neutral-text-secondary'}`}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </span>
        )}
      </div>
    </th>
  );
};

// ==================== Table Cell ====================
interface TableCellProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
  colSpan?: number;
  truncate?: boolean;
  maxWidth?: string;
}

const TableCell: React.FC<TableCellProps> = ({
  children,
  className = '',
  align = 'left',
  colSpan,
  truncate = false,
  maxWidth,
}) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <td
      colSpan={colSpan}
      style={maxWidth ? { maxWidth } : undefined}
      className={`
        px-4 py-3 text-sm text-neutral-text-primary
        ${alignClasses[align]}
        ${truncate ? 'truncate' : ''}
        ${className}
      `}
    >
      {children}
    </td>
  );
};

// ==================== Table Empty State ====================
interface TableEmptyProps {
  colSpan: number;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  action?: React.ReactNode;
}

const TableEmpty: React.FC<TableEmptyProps> = ({
  colSpan,
  icon,
  title = 'Tidak ada data',
  description = 'Belum ada data yang tersedia saat ini.',
  action,
}) => {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-16 text-center">
        <div className="flex flex-col items-center">
          {icon ? (
            <div className="mb-3 text-neutral-border">{icon}</div>
          ) : (
            <svg
              className="w-12 h-12 mb-3 text-neutral-border"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          )}
          <p className="text-sm font-semibold text-neutral-text-primary">{title}</p>
          <p className="text-xs text-neutral-text-secondary mt-1 max-w-sm">{description}</p>
          {action && <div className="mt-4">{action}</div>}
        </div>
      </td>
    </tr>
  );
};

// ==================== Assign Sub-components ====================
Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Header = TableHeader;
Table.Cell = TableCell;
Table.Empty = TableEmpty;

export default Table;